/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useApp } from "../contexts/AppContext";
import { stepLn } from "../lib/overrides";
import { ProtocolProgress } from "../components/ProtocolProgress";
import { speak } from "../lib/i18n";
import { postOrQueue } from "../lib/offline";
import { loadManifest, mediaUrl, isVideoCached, playStepAudio } from "../lib/media";
import { toast } from "sonner";
import {
  Activity, Footprints, HandHelping, ChevronRight, Volume2, X, Trophy, ArrowLeft, Play, Lock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Illustration } from "../components/ExerciseIllustrations";
import ShellOverlay from "../components/ShellOverlay";
import { ProgrammeDuJour } from "../components/programme/ProgrammeDuJour";

const DOMAIN_META = {
  equilibre: { icon: Activity, color: "#2BA3A0" },
  marche: { icon: Footprints, color: "#0A1426" },
  avq: { icon: HandHelping, color: "#D4AF5A" },
};

function DomainCard({ d, onOpen, sessions }) {
  const meta = DOMAIN_META[d.key];
  const Icon = meta.icon;
  const count = sessions.filter((s) => s.domaine === d.key).length;
  const progress = Math.min(100, count * 12);
  return (
    <button
      onClick={onOpen}
      data-testid={`domain-${d.key}-card`}
      className="w-full bg-white rounded-2xl border border-[#E4E0D5] p-4 text-left flex items-center gap-3 hover:border-[#2BA3A0] transition-colors"
    >
      <div className="grid place-items-center w-14 h-14 rounded-2xl" style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}>
        <Icon size={26} />
      </div>
      <div className="flex-1">
        <div className="text-[15px] font-bold text-[#0A1426]">{d.title}</div>
        <div className="text-[11px] text-[#0A1426]/60 mb-1.5">{d.exercises.length} exercice(s) · {count} séance(s)</div>
        <Progress value={progress} className="h-2 bg-[#F5F3EE]" />
      </div>
      <ChevronRight size={22} className="text-[#0A1426]/40" />
    </button>
  );
}

function DomainPreview({ domain, onStart, onClose }) {
  return (
    <ShellOverlay testid={`domain-preview-${domain.key}`}>
      <div className="absolute inset-0 bg-[#F5F3EE] flex flex-col">
        <div className="px-5 pt-4 pb-3 flex items-center justify-between bg-white border-b border-[#E4E0D5]">
          <button data-testid="domain-preview-back" onClick={onClose} className="w-11 h-11 grid place-items-center rounded-full bg-[#F5F3EE] text-[#0A1426]">
            <ArrowLeft size={22} />
          </button>
          <div className="font-[Fraunces] text-lg font-bold text-[#0A1426] leading-tight">{domain.title}</div>
        </div>
        <div className="flex-1 overflow-y-auto bc-scroll px-5 pt-5 pb-8 flex flex-col gap-5">
          {domain.exercises.map((exo) => (
            <div key={exo.id} data-testid={`exo-${exo.id}`} className="bg-white rounded-3xl border border-[#E4E0D5] p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-[Fraunces] text-[19px] font-bold text-[#0A1426] leading-tight">{exo.title}</div>
                  <div className="text-[12px] text-[#0A1426]/60 mt-0.5">{exo.steps.length} étapes · {exo.duration}{exo.video ? " · vidéo" : ""}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {exo.steps.map((s, i) => (
                  <div key={i} data-testid={`preview-step-${exo.id}-${i + 1}`} className="bg-[#F5F3EE] rounded-2xl p-2 flex flex-col">
                    <div className="relative aspect-square">
                      <img src={mediaUrl(s.svg)} alt={s.caption} className="w-full h-full object-contain" loading="lazy" />
                      <span className="absolute top-1 left-1 w-6 h-6 rounded-full bg-[#2BA3A0] text-white text-[11px] font-bold grid place-items-center">{s.n}</span>
                    </div>
                    <div className="mt-1 text-center font-bold text-[#0A1426] text-[12px] leading-tight">{s.caption}</div>
                  </div>
                ))}
              </div>
              <Button data-testid={`start-${exo.id}`} onClick={() => onStart(exo)}
                className="w-full h-14 mt-4 rounded-full bg-[#2BA3A0] hover:bg-[#0A1426] text-white text-base font-bold">
                <Play size={18} className="mr-2" /> Commencer
              </Button>
            </div>
          ))}
        </div>
      </div>
    </ShellOverlay>
  );
}

function StepMedia({ exo, step, idx, canVideo, online, cached, lockedVideo, onUpgrade }) {
  const videoAvailable = Boolean(exo.video) && (online || cached) && !lockedVideo;
  return (
    <div className="w-full max-w-[260px] mt-2 mb-3">
      <div className="aspect-square bg-white rounded-3xl border border-[#E4E0D5] shadow-sm p-3 relative overflow-hidden">
        {videoAvailable ? (
          <video key={exo.video} data-testid="exercise-video" src={mediaUrl(exo.video)} className="w-full h-full object-cover rounded-2xl"
            autoPlay loop muted playsInline controls />
        ) : (
          <img data-testid="exercise-step-illustration" src={mediaUrl(step.svg)} alt={step.caption} className="w-full h-full object-contain" />
        )}
        <span data-testid="video-watermark" className="absolute bottom-4 right-4 px-2 py-0.5 rounded-md bg-[#0A1426]/55 text-[#D4AF5A] text-[11px] font-[Fraunces] font-bold tracking-wide pointer-events-none select-none">Bombacare</span>
        <span className="absolute top-3 left-3 w-7 h-7 rounded-full bg-[#2BA3A0] text-white text-[12px] font-bold grid place-items-center">{step.n}</span>
      </div>
      {exo.video && (
        <div className="mt-2 flex flex-col items-center gap-1">
          {lockedVideo ? (
            <button data-testid="exercise-video-locked" onClick={onUpgrade}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF5A]/20 text-[#8A6D2B] text-[12px] font-bold">
              <Lock size={14} /> Vidéo — forfait Famille
            </button>
          ) : videoAvailable ? (
            <span className="text-[10px] text-[#0A1426]/60 text-center">Démonstration vidéo — suivez les consignes de votre praticien</span>
          ) : (
            <span data-testid="exercise-video-unavailable" className="text-[11px] text-[#0A1426]/60">Vidéo disponible en ligne ou après téléchargement Wi-Fi</span>
          )}
        </div>
      )}
    </div>
  );
}

function ExercisePlayer({ exo, onClose, lang, domaineKey, patientId }) {
  const navigate = useNavigate();
  const { online, hasFeature, aidantMode } = useApp();
  const [idx, setIdx] = useState(0);
  const [done, setDone] = useState(false);
  const [douleur, setDouleur] = useState(null);
  const [saved, setSaved] = useState(false);
  const [cached, setCached] = useState(false);
  const step = exo.steps[idx];
  const ln = lang === "ln";
  const text = aidantMode ? (ln && stepLn(exo.id, step, true)) || step.text_aidant : (ln && stepLn(exo.id, step)) || step.text;
  const audioRel = ln ? step.audio_ln : step.audio;
  const isLast = idx === exo.steps.length - 1;
  const canVideo = hasFeature("videos");
  const save = async (d) => {
    await postOrQueue("/exercise-sessions", {
      patient_id: patientId, domaine: domaineKey, exercice_id: exo.id, douleur: d,
      etapes_faites: exo.steps.length, etapes_total: exo.steps.length, points: 120,
      source: aidantMode ? "aidant" : "app", client_date: new Date().toISOString(),
    });
    setSaved(true);
  };

  useEffect(() => { isVideoCached(exo.video).then(setCached); }, [exo.video]);

  if (done) {
    return (
      <ShellOverlay>
        <div className="absolute inset-0 bg-[#0A1426]/95 text-white flex flex-col items-center justify-center px-6 text-center">
          {!saved ? (
            <>
              <h2 className="font-[Fraunces] text-3xl font-bold">{ln ? "Mpasi boni ?" : "Votre douleur maintenant ?"}</h2>
              <p className="text-base mt-1 text-white/80">0 = aucune · 10 = insupportable</p>
              <div data-testid="pain-scale" className="grid grid-cols-6 gap-2 mt-5 max-w-sm">
                {Array.from({ length: 11 }, (_, i) => (
                  <button key={i} data-testid={`pain-${i}`} onClick={() => setDouleur(i)} className={`h-14 rounded-2xl text-xl font-bold border-2 ${douleur === i ? "bg-[#D4AF5A] text-[#0A1426] border-[#D4AF5A]" : "border-white/30 text-white"} ${i >= 7 ? "bg-[#D9534F]/40" : i >= 4 ? "bg-[#D4AF5A]/25" : "bg-white/10"}`}>{i}</button>
                ))}
              </div>
              <div className="flex gap-2 mt-6">
                <Button data-testid="pain-skip" variant="outline" onClick={() => save(null)} className="h-14 px-6 rounded-full border-white/40 bg-transparent text-white font-bold">Passer</Button>
                <Button data-testid="pain-save" disabled={douleur === null} onClick={() => save(douleur)} className="h-14 px-8 rounded-full bg-white text-[#0A1426] font-bold hover:bg-[#F5F3EE]">Enregistrer</Button>
              </div>
            </>
          ) : (
            <>
              <div className="w-40 h-40 mb-2"><Illustration name="celebrate" /></div>
              <h2 className="font-[Fraunces] text-4xl font-bold">Bravo !</h2>
              <p className="text-lg mt-2 text-white/85">{online ? "Séance enregistrée" : "Séance enregistrée — sera envoyée au retour du réseau"}{douleur != null ? ` · douleur ${douleur}/10` : ""}</p>
              {douleur >= 7 && <p data-testid="pain-alert" className="mt-2 text-[14px] bg-[#D9534F] rounded-2xl px-4 py-2">Douleur forte : votre praticien est informé. Arrêtez si elle persiste.</p>}
              <div className="mt-6 px-5 py-2 rounded-full bg-[#D4AF5A] text-[#0A1426] font-bold text-lg">+120 points</div>
              <Button data-testid="exercise-finish-close" onClick={onClose} className="mt-8 h-14 px-10 rounded-full bg-white text-[#0A1426] font-bold hover:bg-[#F5F3EE]">
                Continuer
              </Button>
            </>
          )}
        </div>
      </ShellOverlay>
    );
  }

  return (
    <ShellOverlay>
      <div className="absolute inset-0 bg-[#F5F3EE] flex flex-col">
        <div className="px-5 pt-4 pb-3 flex items-center justify-between border-b border-[#E4E0D5] bg-white shrink-0">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold">{exo.duration}</div>
            <div className="font-[Fraunces] text-lg font-bold text-[#0A1426]">{exo.title}</div>
          </div>
          <button data-testid="exercise-close-btn" onClick={onClose} className="w-11 h-11 grid place-items-center rounded-full bg-[#F5F3EE] text-[#0A1426]">
            <X size={22} />
          </button>
        </div>

        <div className="px-5 py-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[11px] font-bold text-[#0A1426]/70">Étape {idx + 1} / {exo.steps.length}</span>
            {aidantMode && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#0A1426] text-white font-bold uppercase">Mode Aidant</span>}
          </div>
          <Progress value={((idx + 1) / exo.steps.length) * 100} className="h-2 bg-[#E4E0D5]" />
        </div>

        <div className="flex-1 px-6 flex flex-col items-center text-center overflow-y-auto bc-scroll pb-4">
          <StepMedia exo={exo} step={step} idx={idx} canVideo={canVideo} online={online} cached={cached}
            lockedVideo={!canVideo} onUpgrade={() => { onClose(); navigate("/app/subscription"); }} />
          <div className="text-[12px] uppercase tracking-wider text-[#0A1426]/60 font-bold">{step.caption}</div>
          <p data-testid="exercise-step-text" className="font-[Fraunces] text-[19px] font-bold text-[#0A1426] leading-tight max-w-sm px-2 mt-1">{text}</p>
          {step.sub && <p className="text-[13px] text-[#0A1426]/70 mt-1">{step.sub}</p>}
          <button data-testid="exercise-listen-btn"
            onClick={() => (aidantMode ? speak(text, lang) : playStepAudio(audioRel, () => speak(text, lang)))}
            className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-[#E4E0D5] text-[#0A1426] font-bold">
            <Volume2 size={20} /> {ln ? "Yoka" : "Écouter"}
          </button>
        </div>

        <div className="px-5 pb-8 pt-2 shrink-0 flex gap-2">
          <Button data-testid="exercise-prev-btn" variant="outline" disabled={idx === 0} onClick={() => setIdx(idx - 1)}
            className="h-16 w-16 rounded-full border-[#E4E0D5] bg-white text-[#0A1426]">
            <ArrowLeft size={22} />
          </Button>
          <Button data-testid="exercise-next-btn"
            onClick={() => {
              if (isLast) setDone(true); else setIdx(idx + 1);
            }}
            className="flex-1 h-16 rounded-full bg-[#2BA3A0] hover:bg-[#0A1426] text-white text-lg font-bold">
            {isLast ? "Terminer" : "Suivant"}
          </Button>
        </div>
      </div>
    </ShellOverlay>
  );
}

export default function ExercisesPage() {
  const { lang, patient } = useApp();
  const [domains, setDomains] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [previewDomain, setPreviewDomain] = useState(null);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    loadManifest().then((m) => {
      setDomains(m.domains.map((d) => ({ ...d, exercises: m.exercises.filter((e) => e.domain === d.manifest_domain) })));
    }).catch(() => toast.error("Impossible de charger les exercices"));
  }, []);

  useEffect(() => {
    if (!patient) return;
    import("../lib/api").then(({ api }) => api.get(`/exercise-sessions?patient_id=${patient.id}`).then(({ data }) => setSessions(data)).catch(() => null));
  }, [patient]);

  const openPreview = (d) => {
    if (!patient) { toast.error("Aucun patient lié à votre compte."); return; }
    setPreviewDomain(d);
  };

  return (
    <div className="bc-in pt-2 pb-6 flex flex-col gap-5">
      <ProgrammeDuJour patient={patient} domains={domains} sessions={sessions} lang={lang} onOpen={(exo, key) => setPlaying({ exo, domaineKey: key })} />
      {patient?.protocole && <ProtocolProgress patientId={patient.id} lang={lang} />}
      <div className="bg-white rounded-3xl border border-[#E4E0D5] p-5 flex items-center gap-4">
        <div className="flex flex-col items-center justify-center w-20 h-20 rounded-2xl bg-[#D4AF5A]/15 text-[#8A6D2B]">
          <Trophy size={28} />
          <span className="text-[10px] font-bold uppercase tracking-wider mt-1">N°{patient?.niveau ?? 1}</span>
        </div>
        <div className="flex-1">
          <div className="font-[Fraunces] text-[22px] font-bold text-[#0A1426] leading-tight">Vos exercices</div>
          <div className="flex items-center gap-3 mt-1 text-[12px] text-[#0A1426]/70 font-medium">
            <span>{patient?.points ?? 0} points</span><span>·</span><span>{patient?.serie_jours ?? 0} jours d'affilée</span>
          </div>
        </div>
      </div>

      <h3 className="text-[12px] uppercase tracking-wider text-[#0A1426]/60 font-bold">Domaines thérapeutiques</h3>
      <div className="flex flex-col gap-3">
        {domains.map((d) => <DomainCard key={d.key} d={d} sessions={sessions} onOpen={() => openPreview(d)} />)}
      </div>

      {previewDomain && (
        <DomainPreview domain={previewDomain}
          onStart={(exo) => { setPlaying({ exo, domaineKey: previewDomain.key }); setPreviewDomain(null); }}
          onClose={() => setPreviewDomain(null)} />
      )}
      {playing && (
        <ExercisePlayer exo={playing.exo} domaineKey={playing.domaineKey} lang={lang}
          patientId={patient?.id} onClose={() => setPlaying(null)} />
      )}
    </div>
  );
}
